import React from 'react';
import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import SkeletonLoader from '../skeletonLoader/SkeletonLoader';
import type { UseOrderAndPaginationControlReturn } from './hooks';
import classNames from '../../utils/classNames';

const tableWrapperClasses = 'px-1.5 rounded';
const skeletonLoaderClasses = 'p-2';
const blockClasses = 'w-full drop-shadow-sm';
const tableHeaderRowClasses = 'mt-2.5 ml-2.5 h-6 px-1';
const tableHeaderCellClasses = 'px-2 text-xs leading-6 text-gray-400 bg-gray-100 font-semibold';
const tableRowClasses = 'py-4';
const tableRowCellClasses = 'h-6 pt-4 px-2 leading-6 text-xs';
const blockHeaderContainerClasses = `flex h-11 flex-row justify-between justify-items-center gap-2.5 rounded-tr py-2 px-2.5 font-black -tracking-widened ${blockClasses}`;
const blockHeaderContentClasses = 'w-32 flex-initial text-neutral-800';
const blockHeaderLeftClasses = 'h-6 w-16 text-base font-semibold leading-6';
const blockHeaderRightClasses = 'h-5 w-28 text-sm font-bold leading-5';

interface BaseTableColumn<Row> {
    title: ReactNode;
    className?: string;
    cellClassName?: string | ((row: Row) => string);
    colSpan?: number;
}

type DataTableColumn<Row> = BaseTableColumn<Row> & {
    key: keyof Row & string;
    render?: (column: TableColumn<Row>, row: Row) => ReactNode;
};

type RenderTableColumn<Row> = BaseTableColumn<Row> & {
    key: string;
    render: (column: TableColumn<Row>, row: Row) => ReactNode;
};

export type TableColumn<Row> = DataTableColumn<Row> | RenderTableColumn<Row>;

function isRenderTableColumn<Row>(column: TableColumn<Row>): column is RenderTableColumn<Row> {
    return typeof column.render === 'function';
}

function getTableCellValue(value: unknown): ReactNode {
    if (value == null) return '';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return value;
    }
    if (React.isValidElement(value)) return value;

    return '';
}

interface SortButtonProps {
    columnKey: string;
    columnTitle: ReactNode;
    orderBy: string | null;
    setOrderBy: Dispatch<SetStateAction<string | null>>;
}

export function SortButtons({
    columnKey,
    columnTitle,
    orderBy,
    setOrderBy,
}: SortButtonProps): JSX.Element {
    const columnTitleText =
        typeof columnTitle === 'string' || typeof columnTitle === 'number'
            ? String(columnTitle)
            : columnKey;
    return (
        <span className="mb-0.5 inline-flex flex-col pl-1 align-middle">
            {[
                {
                    orderPrefix: '',
                    buttonClassName: 'h-1.5',
                    icon: faSortUp,
                    iconClassName: `block h-3`,
                    ariaLabel: interpolate(
                        gettext('SORT_COLUMN_ASCENDING'),
                        { name: columnTitleText },
                        true
                    ),
                },
                {
                    orderPrefix: '-',
                    buttonClassName: 'relative h-1.5 overflow-hidden',
                    icon: faSortDown,
                    iconClassName: `absolute bottom-0 block h-3`,
                    ariaLabel: interpolate(
                        gettext('SORT_COLUMN_DESCENDING'),
                        { name: columnTitleText },
                        true
                    ),
                },
            ].map(({ orderPrefix, buttonClassName, icon, iconClassName, ariaLabel }, index) => {
                const whenSelected = `${orderPrefix}${columnKey}`;
                return (
                    <button
                        key={`${columnKey}-${index}`}
                        onClick={() => {
                            setOrderBy((current) =>
                                current === whenSelected ? null : whenSelected
                            );
                        }}
                        className={buttonClassName}
                        aria-label={ariaLabel}
                    >
                        <FontAwesomeIcon
                            icon={icon}
                            className={classNames(
                                iconClassName,
                                orderBy === whenSelected ? 'text-black' : 'text-gray-400'
                            )}
                        />
                    </button>
                );
            })}
        </span>
    );
}

interface TableHeaderProps<Row> {
    columns: TableColumn<Row>[];
    orderAndPaginationControl?: UseOrderAndPaginationControlReturn | null;
    headerRowClasses: string;
    headerCellClasses: string;
}

function TableHeader<Row>({
    columns,
    orderAndPaginationControl = null,
    headerRowClasses,
    headerCellClasses,
}: TableHeaderProps<Row>): JSX.Element {
    return (
        <thead>
            <tr className={headerRowClasses}>
                {columns.map((column, columnIndex) => (
                    <th
                        key={columnIndex}
                        colSpan={column.colSpan}
                        className={classNames(
                            headerCellClasses,
                            columnIndex + 1 === columns.length ? 'text-right' : 'text-left',
                            column.className
                        )}
                    >
                        {column.title}
                        {orderAndPaginationControl?.sortableFields.includes(column.key) && (
                            <SortButtons
                                columnKey={column.key}
                                columnTitle={column.title}
                                orderBy={orderAndPaginationControl.orderBy}
                                setOrderBy={orderAndPaginationControl.setOrderBy}
                            />
                        )}
                    </th>
                ))}
            </tr>
        </thead>
    );
}

function isLastColumn<Row>(index: number, columns: TableColumn<Row>[]): boolean {
    return index + 1 === columns.length;
}

interface TableRowProps<Row> {
    columns: TableColumn<Row>[];
    data: Row[];
    rowClasses: string;
    rowCellClasses: string;
    renderExpandedRows?: (row: Row, rowIndex: number) => ReactNode;
    getRowKey?: (row: Row, rowIndex: number) => string;
    onRowClick?: (row: Row, event?: React.MouseEvent<HTMLTableRowElement>) => void;
}

function TableRow<Row>({
    columns,
    data,
    rowClasses,
    rowCellClasses,
    renderExpandedRows,
    getRowKey,
    onRowClick,
}: TableRowProps<Row>): JSX.Element {
    return (
        <>
            {data.map((rowData, rowIndex) => {
                const rowKey = getRowKey?.(rowData, rowIndex) ?? `table-body-${rowIndex}`;

                return (
                    <React.Fragment key={rowKey}>
                        <tr
                            className={classNames(rowClasses, onRowClick && 'cursor-pointer')}
                            onClick={onRowClick ? (event) => onRowClick(rowData, event) : undefined}
                        >
                            {columns.map((column, columnIndex) => (
                                <td
                                    key={`${rowKey}-${columnIndex}`}
                                    colSpan={column.colSpan}
                                    className={classNames(
                                        rowCellClasses,
                                        typeof column.cellClassName === 'function'
                                            ? column.cellClassName(rowData)
                                            : column.cellClassName,
                                        isLastColumn(columnIndex, columns) && 'text-right'
                                    )}
                                >
                                    {isRenderTableColumn(column)
                                        ? column.render(column, rowData)
                                        : getTableCellValue(rowData[column.key])}
                                </td>
                            ))}
                        </tr>
                        {renderExpandedRows?.(rowData, rowIndex)}
                    </React.Fragment>
                );
            })}
        </>
    );
}

interface BlockHeaderProps {
    header?: string;
}

function BlockHeader({ header }: BlockHeaderProps): JSX.Element {
    return (
        <div className={blockHeaderContainerClasses}>
            <div className={blockHeaderContentClasses}>
                <label className={blockHeaderLeftClasses}>{header}</label>
            </div>
            <div className={`${blockHeaderContentClasses} text-right`}>
                <label className={blockHeaderRightClasses}>Download CSV</label>
                <FontAwesomeIcon icon={faDownload} className="h-3 w-3 text-gray-400" />
            </div>
        </div>
    );
}

export type TableProps<Row> = {
    data: Row[];
    columns: TableColumn<Row>[];
    header?: string;
    showHeader?: boolean;
    orderAndPaginationControl?: UseOrderAndPaginationControlReturn | null;
    loading: boolean;
    showBorder?: boolean;
    showDivider?: boolean;
    wrapperClasses?: string;
    loaderClasses?: string;
    headerRowClasses?: string;
    headerCellClasses?: string;
    rowClasses?: string;
    rowCellClasses?: string;
    /** Widths of the underlying grid columns; columns with a `colSpan` cover several of them. */
    columnWidths?: string[];
    /** Rendered directly after the row, so it must return `<tr>` elements. */
    renderExpandedRows?: (row: Row, rowIndex: number) => ReactNode;
    getRowKey?: (row: Row, rowIndex: number) => string;
    onRowClick?: (row: Row, event?: React.MouseEvent<HTMLTableRowElement>) => void;
};

function Table<Row extends object = object>({
    data,
    columns,
    header,
    showHeader = false,
    orderAndPaginationControl = null,
    loading,
    showBorder = true,
    showDivider = true,
    wrapperClasses = tableWrapperClasses,
    loaderClasses = skeletonLoaderClasses,
    headerRowClasses = tableHeaderRowClasses,
    headerCellClasses = tableHeaderCellClasses,
    rowClasses = tableRowClasses,
    rowCellClasses = tableRowCellClasses,
    columnWidths,
    renderExpandedRows,
    getRowKey,
    onRowClick,
}: TableProps<Row>): JSX.Element {
    return (
        <div className={classNames(wrapperClasses, showBorder && 'border')}>
            {showHeader && <BlockHeader header={header} />}
            {showDivider && <hr className="border-gray-200 pb-2" />}
            {loading ? (
                <div className="pb-1.5 text-center">
                    <SkeletonLoader count={3} wrapperClassName={loaderClasses} />
                </div>
            ) : (
                <table
                    className={classNames(
                        blockClasses,
                        'mb-2.5 rounded-bl',
                        // Column widths only apply to a fixed table layout.
                        columnWidths && 'table-fixed'
                    )}
                >
                    {columnWidths && (
                        <colgroup>
                            {columnWidths.map((width, columnIndex) => (
                                <col key={columnIndex} style={{ width }} />
                            ))}
                        </colgroup>
                    )}
                    <TableHeader
                        columns={columns}
                        orderAndPaginationControl={orderAndPaginationControl}
                        headerRowClasses={headerRowClasses}
                        headerCellClasses={headerCellClasses}
                    />
                    <tbody>
                        <TableRow
                            columns={columns}
                            data={data}
                            rowClasses={rowClasses}
                            rowCellClasses={rowCellClasses}
                            renderExpandedRows={renderExpandedRows}
                            getRowKey={getRowKey}
                            onRowClick={onRowClick}
                        />
                    </tbody>
                </table>
            )}
        </div>
    );
}

export default Table;
